import type { PriceSeries } from "@ainkauf/core";

/** Eine Farbe je Laden. Dieselbe Reihenfolge wie in der Liste darunter. */
export const LINIENFARBEN = ["var(--food-basil)", "var(--food-sea)", "var(--food-plum)", "var(--food-carrot)", "var(--food-berry)"];

const BREITE = 320;
const HOEHE = 150;
const RAND = { oben: 12, unten: 26, links: 44, rechts: 10 };

const euro = (cents: number) => (cents / 100).toLocaleString("de-DE", { style: "currency", currency: "EUR" });
const datum = (iso: string) => new Date(iso).toLocaleDateString("de-DE", { day: "numeric", month: "short" });

/**
 * Preisverlauf als Liniendiagramm, eine Linie je Laden.
 *
 * Bewusst von Hand gezeichnet statt mit einer Diagramm-Bibliothek. Stufe 4 der
 * Prompts schlägt Recharts vor, das wären rund hundert Kilobyte für fünf
 * Linien und zwei Achsen. Eine Offline-App lädt beim Start alles, was sie
 * je braucht, deshalb bleibt es wie bei den Monatsbalken in den Ausgaben bei
 * eigenem SVG.
 *
 * Die Zeitachse ist echt skaliert und nicht in gleichen Abständen: Zwei Preise
 * aus derselben Woche sollen nebeneinander liegen und nicht so aussehen, als
 * läge ein halbes Jahr dazwischen.
 */
export function PriceChart({ series, label }: { series: readonly PriceSeries[]; label: (storeId: string | null) => string }) {
  const punkte = series.flatMap((reihe) => reihe.points);
  if (punkte.length === 0) return null;

  const zeiten = punkte.map((punkt) => Date.parse(punkt.observedAt));
  const preise = punkte.map((punkt) => punkt.priceCents);

  const vonZeit = Math.min(...zeiten);
  const bisZeit = Math.max(...zeiten);
  const minPreis = Math.min(...preise);
  const maxPreis = Math.max(...preise);

  // Etwas Luft nach oben und unten, damit die Linie nicht am Rahmen klebt.
  // Bei nur einem Preis wäre die Spanne null, dann wird eine erfunden.
  const spanne = maxPreis - minPreis || Math.max(maxPreis * 0.2, 50);
  const unten = minPreis - spanne * 0.15;
  const oben = maxPreis + spanne * 0.15;

  const x = (iso: string) => {
    if (bisZeit === vonZeit) return RAND.links + (BREITE - RAND.links - RAND.rechts) / 2;
    const anteil = (Date.parse(iso) - vonZeit) / (bisZeit - vonZeit);
    return RAND.links + anteil * (BREITE - RAND.links - RAND.rechts);
  };
  const y = (cents: number) => {
    const anteil = (cents - unten) / (oben - unten);
    return HOEHE - RAND.unten - anteil * (HOEHE - RAND.oben - RAND.unten);
  };

  return (
    <figure className="karte m-0 px-3 py-3">
      <svg viewBox={`0 0 ${String(BREITE)} ${String(HOEHE)}`} className="h-auto w-full" role="img" aria-label="Preisverlauf">
        {/* Zwei Hilfslinien, mehr braucht es nicht: teuerster und günstigster
            beobachteter Preis. Ein volles Raster lenkt nur ab. */}
        {[maxPreis, minPreis].map((wert) => (
          <g key={wert}>
            <line x1={RAND.links} x2={BREITE - RAND.rechts} y1={y(wert)} y2={y(wert)} stroke="var(--paper-2)" strokeWidth="1" />
            <text x={RAND.links - 6} y={y(wert) + 4} textAnchor="end" fontSize="10" fill="var(--ink-3)">
              {euro(wert)}
            </text>
          </g>
        ))}

        <text x={RAND.links} y={HOEHE - 8} fontSize="10" fill="var(--ink-3)">
          {datum(new Date(vonZeit).toISOString())}
        </text>
        {bisZeit !== vonZeit && (
          <text x={BREITE - RAND.rechts} y={HOEHE - 8} textAnchor="end" fontSize="10" fill="var(--ink-3)">
            {datum(new Date(bisZeit).toISOString())}
          </text>
        )}

        {series.map((reihe, i) => {
          const farbe = LINIENFARBEN[i % LINIENFARBEN.length];
          const pfad = reihe.points.map((punkt) => `${String(x(punkt.observedAt))},${String(y(punkt.priceCents))}`).join(" ");

          return (
            <g key={reihe.storeId ?? "ohne"}>
              {reihe.points.length > 1 && (
                <polyline points={pfad} fill="none" stroke={farbe} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
              )}
              {reihe.points.map((punkt) => (
                <circle
                  key={`${punkt.observedAt}-${String(punkt.priceCents)}`}
                  cx={x(punkt.observedAt)}
                  cy={y(punkt.priceCents)}
                  r="3.5"
                  fill={farbe}
                />
              ))}
            </g>
          );
        })}
      </svg>

      <figcaption className="mt-1 flex flex-wrap gap-x-3 gap-y-1 px-1 text-xs text-ink-2">
        {series.map((reihe, i) => (
          <span key={reihe.storeId ?? "ohne"} className="inline-flex items-center gap-1.5">
            <span aria-hidden className="size-2.5 rounded-full" style={{ background: LINIENFARBEN[i % LINIENFARBEN.length] }} />
            {label(reihe.storeId)}
          </span>
        ))}
      </figcaption>
    </figure>
  );
}
